import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  getPrompts,
  createPrompt,
  deletePrompt,
  PromptListItem,
  PromptCreate,
  UseCase,
} from '../services/api'
import { useWorkflow } from '../contexts/WorkflowContext'

// Define which use_cases belong to each workflow
const DELIVERY_USE_CASES = ['waf', 'caf', 'aidw', 'security-assessment', 'maturity-assessment', 'azure', 'm365']
// System-level categories are infrastructure prompts (managed via GeekMode), not user-facing
const SYSTEM_CATEGORIES = ['system', 'ai-ify', 'user-prompt']
const PRE_SALES_USE_CASES = ['sow', 'proposal', 'wbs', 'discovery', 'pricing', 'managed-services']

const DELIVERY_USE_CASE_OPTIONS: { value: UseCase | ''; label: string }[] = [
  { value: '', label: 'All Use Cases' },
  { value: 'waf', label: 'WAF' },
  { value: 'caf', label: 'CAF' },
  { value: 'aidw', label: 'AIDW' },
  { value: 'security-assessment', label: 'Security Assessment' },
  { value: 'other', label: 'Other' },
]

const PRE_SALES_USE_CASE_OPTIONS: { value: UseCase | ''; label: string }[] = [
  { value: '', label: 'All Use Cases' },
  { value: 'sow', label: 'SOW' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'wbs', label: 'WBS' },
  { value: 'managed-services', label: 'Managed Services' },
  { value: 'other', label: 'Other' },
]

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'executive_summary', label: 'Executive Summary' },
  { value: 'migration_strategy', label: 'Migration Strategy' },
  { value: 'architecture', label: 'Architecture' },
  { value: 'security', label: 'Security' },
  { value: 'governance', label: 'Governance' },
  { value: 'operations', label: 'Operations' },
  { value: 'other', label: 'Other' },
]

const useCaseBadgeColors: Record<string, { bg: string; text: string; border: string }> = {
  // Delivery
  aidw: { bg: 'rgba(251, 191, 36, 0.2)', text: '#fbbf24', border: 'rgba(251, 191, 36, 0.3)' },
  caf: { bg: 'rgba(59, 130, 246, 0.2)', text: '#60a5fa', border: 'rgba(59, 130, 246, 0.3)' },
  waf: { bg: 'rgba(34, 197, 94, 0.2)', text: '#22c55e', border: 'rgba(34, 197, 94, 0.3)' },
  'security-assessment': { bg: 'rgba(239, 68, 68, 0.2)', text: '#ef4444', border: 'rgba(239, 68, 68, 0.3)' },
  // Pre-Sales
  sow: { bg: 'rgba(16, 185, 129, 0.2)', text: '#10b981', border: 'rgba(16, 185, 129, 0.3)' },
  proposal: { bg: 'rgba(20, 184, 166, 0.2)', text: '#14b8a6', border: 'rgba(20, 184, 166, 0.3)' },
  wbs: { bg: 'rgba(52, 211, 153, 0.2)', text: '#34d399', border: 'rgba(52, 211, 153, 0.3)' },
  'managed-services': { bg: 'rgba(6, 182, 212, 0.2)', text: '#06b6d4', border: 'rgba(6, 182, 212, 0.3)' },
  other: { bg: 'rgba(148, 163, 184, 0.2)', text: '#94a3b8', border: 'rgba(148, 163, 184, 0.3)' },
}

export default function Prompts() {
  const queryClient = useQueryClient()
  const { isPreSales } = useWorkflow()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [useCaseFilter, setUseCaseFilter] = useState<string>('')
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [includeInactive, setIncludeInactive] = useState(false)
  const [search, setSearch] = useState('')
  // Local prompt mode toggle — visible on the page, initialized from global workflow
  const [promptMode, setPromptMode] = useState<'delivery' | 'pre_sales'>(isPreSales ? 'pre_sales' : 'delivery')

  const isShowingPreSales = promptMode === 'pre_sales'

  // Get use case options based on local prompt mode
  const USE_CASE_OPTIONS = isShowingPreSales ? PRE_SALES_USE_CASE_OPTIONS : DELIVERY_USE_CASE_OPTIONS
  const workflowUseCases = isShowingPreSales ? PRE_SALES_USE_CASES : DELIVERY_USE_CASES

  // Reset filters when switching prompt modes
  useEffect(() => {
    setUseCaseFilter('')
    setCategoryFilter('')
  }, [promptMode])

  const [formData, setFormData] = useState<PromptCreate>({
    name: '',
    display_name: '',
    description: '',
    category: 'other',
    use_case: isShowingPreSales ? 'sow' : 'caf',
    initial_content: '',
  })

  const { data: prompts = [], isLoading } = useQuery({
    queryKey: ['prompts', useCaseFilter, categoryFilter, includeInactive, promptMode],
    queryFn: async () => {
      const response = await getPrompts({
        use_case: useCaseFilter || undefined,
        category: categoryFilter || undefined,
        include_inactive: includeInactive,
      })
      // Hide system/infrastructure prompts and filter by workflow
      return response.data.filter(p => {
        if (SYSTEM_CATEGORIES.includes(p.category)) return false
        return workflowUseCases.includes(p.use_case)
      })
    },
  })

  const createMutation = useMutation({
    mutationFn: createPrompt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] })
      setShowCreateModal(false)
      resetForm()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deletePrompt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] })
    },
  })

  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      description: '',
      category: 'other',
      use_case: 'other',
      initial_content: '',
    })
  }

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate(formData)
  }

  const filteredPrompts = prompts.filter(prompt => {
    if (!search) return true
    const searchLower = search.toLowerCase()
    return (
      prompt.name.toLowerCase().includes(searchLower) ||
      prompt.display_name.toLowerCase().includes(searchLower) ||
      (prompt.description?.toLowerCase().includes(searchLower) ?? false)
    )
  })

  const cardStyle = {
    background: 'rgba(15, 23, 42, 0.6)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(100, 180, 255, 0.1)',
    borderRadius: '16px',
  }

  const inputStyle = {
    background: 'rgba(30, 41, 59, 0.5)',
    border: '1px solid rgba(100, 180, 255, 0.15)',
    color: '#e2e8f0',
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: '#f1f5f9' }}>
            Prompt Library
          </h1>
          <p className="mt-1 text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
            Manage and version your AI prompts
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-200 hover:scale-105"
          style={{
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.8) 0%, rgba(139, 92, 246, 0.8) 100%)',
            color: 'white',
            boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
          }}
        >
          <i className="fa-solid fa-plus"></i>
          <span>New Prompt</span>
        </button>
      </div>

      {/* Workflow Mode Toggle */}
      <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
        <button
          onClick={() => setPromptMode('delivery')}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
          style={promptMode === 'delivery' ? {
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.2) 100%)',
            color: '#60a5fa',
            border: '1px solid rgba(96, 165, 250, 0.3)',
            boxShadow: '0 0 15px rgba(59, 130, 246, 0.15)',
          } : {
            background: 'transparent',
            color: 'rgba(148, 163, 184, 0.6)',
            border: '1px solid transparent',
          }}
        >
          <i className="fa-solid fa-briefcase"></i>
          <span>Delivery</span>
          <span className="text-xs px-1.5 py-0.5 rounded" style={{
            background: promptMode === 'delivery' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(100, 116, 139, 0.2)',
          }}>CAF / WAF / AIDW</span>
        </button>
        <button
          onClick={() => setPromptMode('pre_sales')}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
          style={promptMode === 'pre_sales' ? {
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(20, 184, 166, 0.2) 100%)',
            color: '#34d399',
            border: '1px solid rgba(52, 211, 153, 0.3)',
            boxShadow: '0 0 15px rgba(16, 185, 129, 0.15)',
          } : {
            background: 'transparent',
            color: 'rgba(148, 163, 184, 0.6)',
            border: '1px solid transparent',
          }}
        >
          <i className="fa-solid fa-handshake"></i>
          <span>Pre-Sales</span>
          <span className="text-xs px-1.5 py-0.5 rounded" style={{
            background: promptMode === 'pre_sales' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(100, 116, 139, 0.2)',
          }}>SOW / Proposal / WBS</span>
        </button>
      </div>

      {/* Filters */}
      <div className="p-4 rounded-xl" style={cardStyle}>
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search prompts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-4 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={inputStyle}
            />
          </div>
          <select
            value={useCaseFilter}
            onChange={(e) => setUseCaseFilter(e.target.value)}
            className="px-4 py-2 rounded-lg text-sm focus:outline-none"
            style={inputStyle}
          >
            {USE_CASE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2 rounded-lg text-sm focus:outline-none"
            style={inputStyle}
          >
            {CATEGORY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <label className="flex items-center space-x-2 text-sm" style={{ color: '#94a3b8' }}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="rounded"
            />
            <span>Show inactive</span>
          </label>
        </div>
      </div>

      {/* Prompts Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredPrompts.length === 0 ? (
        <div className="text-center py-16 rounded-xl" style={cardStyle}>
          <i className="fa-solid fa-clipboard-list text-4xl mb-4" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
          <p style={{ color: '#94a3b8' }}>No prompts found</p>
          <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
            Create your first prompt to get started
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPrompts.map((prompt) => (
            <PromptCard
              key={prompt.id}
              prompt={prompt}
              onDelete={() => {
                if (confirm(`Delete prompt "${prompt.display_name}"?`)) {
                  deleteMutation.mutate(prompt.id)
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-6" style={cardStyle}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold" style={{ color: '#f1f5f9' }}>Create New Prompt</h2>
              <button onClick={() => { setShowCreateModal(false); resetForm() }} style={{ color: '#94a3b8' }}>
                <i className="fa-solid fa-times text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                    Name (unique identifier)
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    placeholder="e.g., executive_summary_v1"
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                    Display Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    placeholder="e.g., Executive Summary Generator"
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={inputStyle}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what this prompt does..."
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={inputStyle}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
                    style={inputStyle}
                  >
                    {CATEGORY_OPTIONS.slice(1).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                    Use Case
                  </label>
                  <select
                    value={formData.use_case}
                    onChange={(e) => setFormData({ ...formData, use_case: e.target.value as UseCase })}
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
                    style={inputStyle}
                  >
                    {USE_CASE_OPTIONS.slice(1).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                  Prompt Content (Initial Version)
                </label>
                <textarea
                  required
                  value={formData.initial_content}
                  onChange={(e) => setFormData({ ...formData, initial_content: e.target.value })}
                  placeholder="Write the AI prompt instructions here..."
                  rows={8}
                  className="w-full px-3 py-2 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={inputStyle}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm() }}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{ color: '#94a3b8', background: 'rgba(100, 116, 139, 0.2)' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.8) 0%, rgba(139, 92, 246, 0.8) 100%)',
                    color: 'white',
                  }}
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Prompt'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

function PromptCard({ prompt, onDelete }: { prompt: PromptListItem; onDelete: () => void }) {
  const colors = useCaseBadgeColors[prompt.use_case] || useCaseBadgeColors.other

  return (
    <div
      className="p-4 rounded-xl transition-all duration-200 hover:scale-[1.02]"
      style={{
        background: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(100, 180, 255, 0.1)',
      }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <Link
            to={`/prompts/${prompt.id}`}
            className="text-base font-medium hover:underline truncate block"
            style={{ color: '#f1f5f9' }}
          >
            {prompt.display_name}
          </Link>
          <p className="text-xs mt-0.5 truncate" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
            {prompt.name}
          </p>
        </div>
        <div className="flex items-center space-x-2 ml-2">
          <span
            className="px-2 py-0.5 rounded text-xs font-medium uppercase"
            style={{
              background: colors.bg,
              color: colors.text,
              border: `1px solid ${colors.border}`,
            }}
          >
            {prompt.use_case}
          </span>
          {!prompt.is_active && (
            <span
              className="px-2 py-0.5 rounded text-xs"
              style={{
                background: 'rgba(239, 68, 68, 0.2)',
                color: '#ef4444',
                border: '1px solid rgba(239, 68, 68, 0.3)',
              }}
            >
              Inactive
            </span>
          )}
        </div>
      </div>

      {prompt.description && (
        <p
          className="text-sm mb-3 line-clamp-2"
          style={{ color: 'rgba(148, 163, 184, 0.8)' }}
        >
          {prompt.description}
        </p>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
          <span>
            <i className="fa-solid fa-layer-group mr-1"></i>
            {prompt.version_count} version{prompt.version_count !== 1 ? 's' : ''}
          </span>
          <span>
            <i className="fa-solid fa-tag mr-1"></i>
            {prompt.category}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Link
            to={`/prompts/${prompt.id}`}
            className="p-1.5 rounded hover:bg-white/10 transition-colors"
            style={{ color: '#60a5fa' }}
            title="View/Edit"
          >
            <i className="fa-solid fa-edit"></i>
          </Link>
          <button
            onClick={onDelete}
            className="p-1.5 rounded hover:bg-white/10 transition-colors"
            style={{ color: '#ef4444' }}
            title="Delete"
          >
            <i className="fa-solid fa-trash"></i>
          </button>
        </div>
      </div>
    </div>
  )
}
